import React from 'react';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';
import Constants from '../../config/constants';
import { FORMATTER_STYLE, getFormatter } from '../../utils/formatter';
import { Grid, Row, Col } from 'react-bootstrap';
import Layout from '../../components/layout/Layout';
import Card from '../../components/card/Card';
import Alert from '../../modal/alert/Alert';
import ProjectBasicInfo from '../../components/project_detail/ProjectBasicInfo';
import ImagePreviewSlider from '../../components/project_detail/ImagePreviewSlider';
import SubmissionCommentModal from '../../modal/project_submission/SubmissionCommentModal';

import {
  EnergyEvaluationResult,
  EvaluationResultDetail,
  SystemEvaluationResult,
} from '../../components/evaluation/EvaluationResult';

import { defaultRejectHandler, getErrorValue } from '../../utils/utility';

import {
  approveProject,
  rejectProject,
  clearErrorProjectInspection,
  getProjectInspection,
} from '../../redux/project_inspection/project_inspection_actions';

import '../../components/default.css';

class ProjectInspectorDetailPage extends React.Component {
  constructor(props) {
    super(props);

    this.defaultStatus = 'unset';

    this.statusMapper = {
      unset: 'status-pending',
      failed: 'status-failed',
      passed: 'status-passed',
    };

    this.state = {
      isShowApproveModal: false,
      isShowRejectModal: false,
      isShowCommentModal: false,
    };
  }

  componentDidMount() {
    this.props.getSubmission();
  }

  componentDidUpdate(prevProps) {
    if (!this.props.isApproving && this.props.isApproving !== prevProps.isApproving && !this.props.errorMessage) {
      this.dismissApproveModal();
    }

    if (!this.props.isRejecting && this.props.isRejecting !== prevProps.isRejecting && !this.props.errorMessage) {
      this.dismissRejectModal();
    }
  }

  renderActionMenu() {
    const { projectSubmission: submission, isLoading, t } = this.props;

    const submittedMenuSet = new Set([
      'project-submission-approve-menu',
      'project-submission-reject-menu',
      'project-submission-comment',
    ]);
    const rejectedMenuSet = new Set(['project-submission-comment']);
    const disabledMenuSet = new Set([]);

    let availableMenuSet = disabledMenuSet;
    if (submission && submission.status) {
      switch (submission.status) {
        case Constants.PROJECT_SUBMISSION.SUBMITTED:
          availableMenuSet = submittedMenuSet;
          break;
        case Constants.PROJECT_SUBMISSION.REJECTED:
          availableMenuSet = rejectedMenuSet;
          break;
        default:
          availableMenuSet = disabledMenuSet;
      }
    }

    const actionMenus = [
      <li key="project-submission-approve-menu">
        <a
          href="#"
          id="project-approve"
          onClick={() => this.setState({ isShowApproveModal: true })}
        >
          <i className="fas fa-check" /> <span className="menu-text">{t('approve project')}</span>
        </a>
      </li>,
      <li key="project-submission-reject-menu">
        <a
          href="#"
          id="project-reject"
          onClick={() => this.setState({ isShowRejectModal: true })}
        >
          <i className="fas fa-times" /> <span className="menu-text">{t('reject project')}</span>
        </a>
      </li>,
      <li key="project-submission-comment">
        <a
          href="#"
          id="project-comment"
          onClick={() => this.setState({ isShowCommentModal: true })}
        >
          <i className="fas fa-comment-alt" /> <span className="menu-text">{t('comment submission')}</span>
        </a>
      </li>,
    ].filter(element => !isLoading && availableMenuSet.has(element.key));

    return actionMenus;
  }

  buildReportTable(data) {
    const reportStyle = {
      fontSize: '1em',
      padding: '7px 15px',
    };

    const rightAlignStyle = {
      ...reportStyle,
      textAlign: 'right',
    };

    return data.map(item => (
      <Row>
        <Col xs={8} sm={8} md={8}>
          <div style={reportStyle}>
            {item[0]}
          </div>
        </Col>
        <Col xs={2} sm={2} md={2}>
          <div style={rightAlignStyle}>
            {item[1]}
          </div>
        </Col>
        <Col xs={2} sm={2} md={2}>
          <div style={rightAlignStyle}>
            {item[2]}
          </div>
        </Col>
      </Row>
    ));
  }

  dismissApproveModal() {
    this.props.clearError();
    this.setState({ isShowApproveModal: false });
  }

  dismissRejectModal() {
    this.props.clearError();
    this.setState({ isShowRejectModal: false });
  }

  dismissCommentModal() {
    this.props.clearError();
    this.setState({ isShowCommentModal: false });
  }

  renderEvaluationStatus(status) {
    const { t } = this.props;
    return (
      <span style={{ fontWeight: "bold" }}>
        {t("overall")}:
        <span className={'project-status-text ' + this.statusMapper[status]}>
          {" " + t(status.toLowerCase()).toUpperCase()}
        </span>
      </span>
    );
  }

  renderStatus(status) {
    const { t } = this.props;
    return (
      <span style={{ fontWeight: "bold" }}>
        <span className={'project-status-text ' + this.statusMapper[status]}>
          {" " + t(status.toLowerCase()).toUpperCase()}
        </span>
      </span>
    );
  }

  static formatCell(row) {
    const formatter = getFormatter(FORMATTER_STYLE.floatFractionDigit2);
    return row.map((value, idx) => idx === 1 ? (value ? formatter(value) : '-') : value)
  }

  renderEnergyConsumption(whole_building_energy) {
    const { t } = this.props;
    const data = [
      [
        t('building energy consumption'),
        whole_building_energy.EPA,
        t('kWh/Year'),
      ],
      [
        t('energy from PV system'),
        whole_building_energy.renewable_energy,
        t('kWh/Year'),
      ],
      [
        t('net energy consumption (evaluated building)'),
        whole_building_energy.net_EPA,
        t('kWh/Year'),
      ],
      [
        t('net energy consumption (reference building)'),
        whole_building_energy.compliance,
        t('kWh/Year'),
      ],
      [],
    ].map(ProjectInspectorDetailPage.formatCell.bind(this));

    const status = [
      t('status'),
      whole_building_energy.status ? this.renderStatus(whole_building_energy.status) : '-',
    ];
    return this.buildReportTable([...data, status]);
  }

  renderEnvelopeSystem(envelope) {
    const { t } = this.props;
    const ottvData = [
      [
        t('ottv (all zone)'),
        envelope.OTTV.all_zone,
        t('W/sq_m'),
      ],
      [
        t('ottv (a/c zone)'),
        envelope.OTTV.ac_zone,
        t('W/sq_m'),
      ],
      [
        t('ottv compliance'),
        envelope.OTTV.compliance,
        t('W/sq_m'),
      ],
      [],
    ].map(ProjectInspectorDetailPage.formatCell.bind(this));

    const ottvStatus = [
      t('ottv status'),
      envelope.OTTV.status ? this.renderStatus(envelope.OTTV.status) : '-',
    ];

    const rttvData = [
      [],
      [
        t('rttv (a/c zone)'),
        envelope.RTTV.ac_zone,
        t('W/sq_m'),
      ],
      [
        t('rttv compliance'),
        envelope.RTTV.compliance,
        t('W/sq_m'),
      ],
      [],
    ].map(ProjectInspectorDetailPage.formatCell.bind(this));

    const rttvStatus = [
      t('rttv status'),
      envelope.RTTV.status ? this.renderStatus(envelope.RTTV.status) : '-',
      '',
    ];

    return this.buildReportTable([...ottvData, ottvStatus, ...rttvData, rttvStatus]);
  }

  renderLightingSystem(lighting_system) {
    const { t } = this.props;
    const data = [
      [
        t('total power'),
        lighting_system.total_power,
        t('W'),
      ],
      [
        t('total building area'),
        lighting_system.area,
        t('sq m'),
      ],
      [
        t('power density'),
        lighting_system.lighting_power_density,
        t('W/sq_m'),
      ],
      [
        t('compliance'),
        lighting_system.compliance,
        t('W/sq_m'),
      ],
      [],
    ].map(ProjectInspectorDetailPage.formatCell.bind(this));

    const status = [
      t('status'),
      lighting_system.status !== null ? this.renderStatus(lighting_system.status) : '-',
      '',
    ];
    return this.buildReportTable([...data, status]);
  }

  renderSubmission(submission, project) {
    const { status: submission_status, description } = submission;
    const { errorMessage, t } = this.props;
    const { images, evaluation } = project;
    const { evaluation_all_system, evaluation_energy_consumption } = evaluation;

    return (
      <Grid>
        <Row className="show-grid">
          <Alert
            show={this.state.isShowApproveModal}
            onHide={() => !this.props.isApproving && this.dismissApproveModal()}
            title={t('approve project')}
            body={`${t('do you want to approve')} ${project.name}?`}
            errorMessage={getErrorValue(errorMessage, 'detail')}
            primaryButtonTitle={this.props.isApproving ? t('approving') : t('approve')}
            primaryButtonStyle="success"
            primaryButtonEnabled={!this.props.isApproving}
            onPrimaryButtonClick={() => this.props.approveProject()}
          />
          <Alert
            show={this.state.isShowRejectModal}
            onHide={() => !this.props.isRejecting && this.dismissRejectModal()}
            title={t('reject project')}
            body={`${t('do you want to reject')} ${project.name}?`}
            errorMessage={getErrorValue(errorMessage, 'detail')}
            primaryButtonTitle={this.props.isRejecting ? t('rejecting') : t('reject')}
            primaryButtonStyle="danger"
            primaryButtonEnabled={!this.props.isRejecting}
            onPrimaryButtonClick={() => this.props.rejectProject()}
          />
          <SubmissionCommentModal
            submission={submission}
            show={this.state.isShowCommentModal}
            onHide={() => !this.props.isCommentCreating && this.dismissCommentModal()}
          />
          <Col xs={12} sm={12} md={12}>
            <Card header={t('submission description')}>
              <div style={{wordWrap:"break-word"}}>
                {description}
              </div>
            </Card>
          </Col>
          <Col xs={12} sm={12} md={6}>
            <Card className="project-summary">
              <ProjectBasicInfo project={{...project, status: submission_status}} />
            </Card>
          </Col>
          <Col xs={12} sm={12} md={6}>
            <Card padding={false}>
              <ImagePreviewSlider images={images} />
            </Card>
          </Col>
        </Row>
        <Row>
          <Col xs={12} sm={12} md={6}>
            <Card header={t('evaluation 1')}
              footer={this.renderEvaluationStatus(evaluation_all_system)}
            >
              <SystemEvaluationResult evaluation={evaluation} t={t} />
            </Card>
          </Col>
          <Col xs={12} sm={12} md={6}>
            <Card header={t('evaluation 2')}
              footer={this.renderEvaluationStatus(evaluation_energy_consumption)}
            >
              <EnergyEvaluationResult evaluation={evaluation} t={t} />
              <div style={{ height: '105px' }}>
              </div>
            </Card>
          </Col>
        </Row>
        <Row>
          <Col xs={12} sm={12} md={12}>
            <Card header={t('evaluation detail')}>
              <EvaluationResultDetail evaluation={evaluation} t={t} />
            </Card>
          </Col>
        </Row>
        <Row>
          <Col xs={12} sm={12} md={12}>
            <Card header={t('building energy consumption')}>
              {this.renderEnergyConsumption(evaluation.whole_building_energy)}
            </Card>
          </Col>
          <Col xs={12} sm={12} md={12}>
            <Card header={t('envelope system')}>
              {this.renderEnvelopeSystem(evaluation.envelope)}
            </Card>
          </Col>
          <Col xs={12} sm={12} md={12}>
            <Card header={t('lighting system')}>
              {this.renderLightingSystem(evaluation.lighting_system)}
            </Card>
          </Col>
        </Row>
      </Grid>
    );
  }

  render() {
    const { projectSubmission, t } = this.props;
    const { archived_project: project } = projectSubmission || {};

    const breadcrumbItems = [
      { name: t('project'), path: '/project_inspections' },
    ];

    let body;
    if (this.props.isLoading) {
      body = <span>{t('loading')}</span>;
    } else if (projectSubmission && project) {
      body = this.renderSubmission(projectSubmission, project);
      breadcrumbItems.push({ name: `${project.name || this.projectId}`, path: '/id' })
    } else {
      body = <h4>{t('project not found')}</h4>;
    }

    return (
      <Layout
        history={this.props.history}
        breadcrumbItems={breadcrumbItems}
        renderActionMenu={() => this.renderActionMenu()}
      >
        {body}
      </Layout>
    );
  }
}

const mapStateToProps = (state, ownProps) => ({
  isLoading: state.projectInspection.isLoading,
  isRejecting: state.projectInspection.isRejecting,
  isApproving: state.projectInspection.isApproving,
  projectSubmission: state.projectInspection.objects[ownProps.match.params.id],
  errorMessage: state.projectInspection.errorMessage,
  isCommentCreating: state.submissionComment.isCreating,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
  getSubmission: () => defaultRejectHandler(dispatch(getProjectInspection(ownProps.match.params.id))),
  rejectProject: () => defaultRejectHandler(dispatch(rejectProject(ownProps.match.params.id))),
  approveProject: () => defaultRejectHandler(dispatch(approveProject(ownProps.match.params.id))),
  clearError: () => dispatch(clearErrorProjectInspection()),
});

export default translate('translations')(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  )(ProjectInspectorDetailPage),
);



// WEBPACK FOOTER //
// ./src/pages/project_inspection_detail/ProjectInspectionDetailPage.js